import { Injectable, computed, inject, signal } from '@angular/core';
import { LocalStorageService } from './local-storage.service';

const STORAGE_KEY = 'Cashalog-theme';

export type ThemeMode = 'light' | 'dark';

function prefersDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storage = inject(LocalStorageService);

  private readonly _theme = signal<ThemeMode>(this.resolveInitialTheme());

  /** Current color theme. */
  readonly theme = this._theme.asReadonly();

  readonly isDark = computed(() => this._theme() === 'dark');

  constructor() {
    this.applyDom(this._theme());
  }

  setTheme(mode: ThemeMode): void {
    this._theme.set(mode);
    this.applyDom(mode);
  }

  toggleTheme(): void {
    this.setTheme(this._theme() === 'light' ? 'dark' : 'light');
  }

  private applyDom(mode: ThemeMode): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.setAttribute('data-theme', mode);
    this.storage.setItem('local', STORAGE_KEY, mode);
  }

  private resolveInitialTheme(): ThemeMode {
    const raw = this.storage.getItem('local', STORAGE_KEY);
    const stored = raw === 'light' || raw === 'dark' ? raw : null;
    return stored ?? (prefersDark() ? 'dark' : 'light');
  }
}
